<script lang="ts">
  // Der Verbrauch der Kontingente (5-Stunden-Fenster, Woche, dazu eigene
  // Wochenfenster einzelner Modelle wie Fable), wie Claude Code ihn meldet.
  // Steht im Baum des Bereichs Claude und oben auf der Übersicht. Farbe erst
  // bei Befund: grau bis 80 %, dann gelb, ab 95 % rot (docs/gestaltung.md,
  // Regel 2).
  //
  // kompakt: je Fenster EINE Zeile (Kurzname, Balken, Prozent), „wieder
  // voll" im Tooltip -- für einen klein gezogenen Abschnitt (BaumVerbrauch).
  //
  // Vorn das eigene Tageslimit „Heute" (Tim 01.11.2026): wie viel vom
  // heutigen Teil der Woche verbraucht ist (tageslimit in claude.ts). Der
  // Aufrufer reicht es herein: der Notzugang (web/recovery) zeigt diesen
  // Baustein ohne Stores und Einstellungen (recovery-importe.test.mjs).
  import { t } from "../i18n.svelte";
  import { prozent, verbrauchTon, wiederVoll, type Tageslimit, type Verbrauch } from "./claude";

  interface Props {
    verbrauch: Verbrauch | undefined;
    jetzt: Date;
    kompakt?: boolean;
    /** das eigene Tageslimit (tageslimitJetzt) mit der Reserve als Anteil */
    tl?: Tageslimit;
    reserve?: number;
  }
  let { verbrauch, jetzt, kompakt = false, tl, reserve = 0 }: Props = $props();

  const fenster = $derived(
    [
      { name: t("claude.verbrauch.fuenf", "5-Stunden-Fenster"), kurz: t("claude.verbrauch.fuenf_kurz", "Aktuell"), f: verbrauch?.fuenf_stunden },
      { name: t("claude.verbrauch.woche", "Woche"), kurz: t("claude.verbrauch.woche", "Woche"), f: verbrauch?.woche },
      ...(verbrauch?.modelle ?? []).map((m) => ({ name: t("claude.verbrauch.woche_modell", "Woche · {modell}").replace("{modell}", m.name), kurz: m.name, f: m })),
    ].filter((x) => x.f),
  );
  const tlText = $derived.by(() => {
    if (!tl) return "";
    const text = tl.letzterTag
      ? t("claude.tageslimit.letzter", "{heute} von {limit} der Woche · letzter Tag, die Reserve steht zu")
      : t("claude.tageslimit.text", "{heute} von {limit} der Woche · {tag} bleiben {reserve}");
    return text
      .replace("{heute}", prozent(tl.heute))
      .replace("{limit}", prozent(tl.limit))
      .replace("{tag}", tl.reserveAm)
          .replace("{reserve}", prozent(reserve));
  });
  const tonKlasse = (ton: string) => (ton === "danger" ? "text-danger" : ton === "warn" ? "text-warning" : "text-fg");
</script>

{#if kompakt}
  <div class="grid gap-1.5">
    {#if tl}
      {@const ton = verbrauchTon(tl.anteil)}
      <div class="zeile t-meta" data-zeile title={`${t("claude.tageslimit", "Heute")}: ${tlText}`}>
        <span class="text-fg truncate">{t("claude.tageslimit", "Heute")}</span>
        <div class="balken" aria-hidden="true"><i class={ton} style:width="{Math.min(100, Math.round(tl.anteil * 100))}%"></i></div>
        <span class="text-right font-semibold tabular-nums {tonKlasse(ton)}">{prozent(tl.anteil)}</span>
      </div>
    {/if}
    {#each fenster as x (x.name)}
      {@const ton = verbrauchTon(x.f?.anteil)}
      <div class="zeile t-meta" data-zeile title={`${x.name}: ${prozent(x.f?.anteil)} · ${wiederVoll(x.f?.bis, jetzt)}`}>
        <span class="text-fg truncate">{x.kurz}</span>
        <div class="balken" aria-hidden="true">
          <i class={ton} style:width="{Math.min(100, Math.round((x.f?.anteil ?? 0) * 100))}%"></i>
        </div>
        <span class="text-right font-semibold tabular-nums {tonKlasse(ton)}">{prozent(x.f?.anteil)}</span>
      </div>
    {:else}
      <p class="text-fg-subtle t-meta" data-zeile>{t("claude.verbrauch.leer", "Steht da, sobald Claude das erste Mal antwortet.")}</p>
    {/each}
  </div>
{:else}
  <div class="grid gap-2">
    {#if tl}
      {@const ton = verbrauchTon(tl.anteil)}
      <div class="grid gap-0.5" data-zeile>
        <div class="flex items-baseline gap-1.5 t-meta">
          <span class="text-fg">{t("claude.tageslimit", "Heute")}</span>
          <span class="ml-auto font-semibold tabular-nums {tonKlasse(ton)}">{prozent(tl.anteil)}</span>
        </div>
        <div class="balken" aria-hidden="true"><i class={ton} style:width="{Math.min(100, Math.round(tl.anteil * 100))}%"></i></div>
        <span class="text-fg-subtle t-meta tabular-nums">{tlText}</span>
      </div>
    {/if}
    {#each fenster as x (x.name)}
      {@const ton = verbrauchTon(x.f?.anteil)}
      <div class="grid gap-0.5" data-zeile>
        <div class="flex items-baseline gap-1.5 t-meta">
          <span class="text-fg">{x.name}</span>
          <span class="ml-auto font-semibold tabular-nums {tonKlasse(ton)}">{prozent(x.f?.anteil)}</span>
        </div>
        <div class="balken" aria-hidden="true">
          <i class={ton} style:width="{Math.min(100, Math.round((x.f?.anteil ?? 0) * 100))}%"></i>
        </div>
        <span class="text-fg-subtle t-meta tabular-nums">{wiederVoll(x.f?.bis, jetzt)}</span>
      </div>
    {:else}
      <p class="text-fg-subtle t-meta" data-zeile>{t("claude.verbrauch.leer", "Steht da, sobald Claude das erste Mal antwortet.")}</p>
    {/each}
  </div>
{/if}

<style>
  .zeile {
    display: grid;
    grid-template-columns: 3.4rem minmax(0, 1fr) 2.6rem;
    align-items: center;
    gap: 0.5rem;
  }
  .balken {
    position: relative;
    height: 5px;
    border-radius: 3px;
    overflow: hidden;
    background: color-mix(in oklab, var(--fg) 9%, transparent);
  }
  .balken i {
    position: absolute;
    inset: 0 auto 0 0;
    border-radius: 3px;
    background: color-mix(in oklab, var(--fg) 42%, transparent);
  }
  .balken i.warn {
    background: var(--warning);
  }
  .balken i.danger {
    background: var(--danger);
  }
</style>
